import React, { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import '../DeckAssembler.linear.css';

// The portal escapes the fixed workspace slots; the root supplies their theme.
// `modal={false}` (Lane 1370, the meeting prep introduction): the rest of the page
// stays usable, so the overlay never takes or traps focus, never makes the page
// inert and lets clicks outside its panel through. A buyer who clicks the deck chat
// while it shows types into the chat; before, the page behind was inert and the
// typed message went nowhere.
export default function FullPlanOverlay({ children, onClose, returnFocusRef, label = 'Meeting plan', className = '',
  modal = true }) {
  const root = useRef(null);
  useEffect(() => {
    const node = root.current;
    const siblings = modal ? [...document.body.children].filter(el => el !== node) : [];
    const inert = siblings.map(el => [el, el.getAttribute('inert')]);
    const overflow = document.body.style.overflow;
    siblings.forEach(el => el.setAttribute('inert', ''));
    if (modal) document.body.style.overflow = 'hidden';
    const focusable = () => [...node.querySelectorAll('button:not(:disabled),a[href],input:not(:disabled),textarea:not(:disabled),select:not(:disabled),summary,[tabindex="0"]')]
      .filter(el => !el.closest('[hidden]'));
    const focusFirst = () => (focusable()[0] || node).focus();
    // Non-modal: focus is returned on close only when it was inside the panel.
    let inside = modal;
    const focusIn = () => { inside = true; };
    const focusOut = event => {
      if (event.relatedTarget && !node.contains(event.relatedTarget)) inside = false;
    };
    const containFocus = event => { if (!node.contains(event.target)) focusFirst(); };
    if (modal) {
      focusFirst();
      document.addEventListener('focusin', containFocus);
    } else {
      node.addEventListener('focusin', focusIn);
      node.addEventListener('focusout', focusOut);
    }
    const key = event => {
      // Native bubble interception also fences the workspace's document undo.
      event.stopPropagation();
      if (event.key === 'Escape') { event.preventDefault(); onClose(); }
      if (modal && event.key === 'Tab') {
        const list = focusable(), first = list[0], last = list[list.length - 1];
        if (!first) { event.preventDefault(); node.focus(); }
        else if (event.shiftKey && (document.activeElement === first || document.activeElement === node)) {
          event.preventDefault(); last.focus();
        } else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); }
      }
    };
    node.addEventListener('keydown', key);
    // Non-modal: Escape also closes it when nothing else has focus (Codex p4 r1).
    // A focused control keeps its own Escape (the deck chat closes, for example).
    const pageEscape = event => {
      if (event.key === 'Escape' && !event.defaultPrevented && !node.contains(event.target)
          && (event.target === document.body || event.target === document.documentElement)) {
        event.preventDefault(); onClose();
      }
    };
    if (!modal) document.addEventListener('keydown', pageEscape);
    return () => {
      node.removeEventListener('keydown', key);
      document.removeEventListener('keydown', pageEscape);
      document.removeEventListener('focusin', containFocus);
      node.removeEventListener('focusin', focusIn);
      node.removeEventListener('focusout', focusOut);
      inert.forEach(([el, value]) => value === null ? el.removeAttribute('inert') : el.setAttribute('inert', value));
      if (modal) document.body.style.overflow = overflow;
      // The opener is unmounted in the expanded arm. Read its NEW ref after
      // React attaches the collapsed arm, rather than focusing a detached node.
      // eslint-disable-next-line react-hooks/exhaustive-deps -- deliberately read the remounted opener
      if (inside) queueMicrotask(() => returnFocusRef.current?.focus());
    };
  }, [onClose, returnFocusRef, modal]);
  return createPortal(<div ref={root} className={`dk dk-v2 dk-full-plan-root ${modal ? '' : 'dk-full-plan-root--open '}${className}`}
    data-deck-full-plan="true" role="dialog" aria-modal={modal ? 'true' : 'false'} aria-label={label}
    tabIndex={-1}>{children}</div>, document.body);
}
